/**
 * Report-card-nudge messages — Feb 2026 mid-month.
 *
 * ONE 4-way admin switch (`site_settings.free_report_card_nudge`)
 * drives TWO audiences:
 *   - `audience: "free"`  → rendered on FreeHome for signed-in free users.
 *   - `audience: "basic"` → rendered on the paid dashboard for Basic-tier
 *                           users only. Standard / Pro / Pro+ / admins /
 *                           anonymous never see the banner.
 *
 * Copy lives here — a single-file edit for future wording tweaks. Keys
 * (`off`, `progress_report`, `term_1`, `term_2`) are locked in step
 * with `_ALLOWED_NUDGE_POSITIONS` on the backend (routes/site_settings.py);
 * don't rename one without the other.
 */

export const NUDGE_POSITIONS = ["off", "progress_report", "term_1", "term_2"];
export const NUDGE_POSITION_ORDER = NUDGE_POSITIONS; // alias for the admin picker
export const DEFAULT_NUDGE_POSITION = "off";

/** Human labels for the admin picker (never shown to end users). */
export const NUDGE_POSITION_LABELS = {
  off: "Off",
  progress_report: "Progress Report",
  term_1: "Term 1 Report",
  term_2: "Term 2 Report",
};

/**
 * Message payloads keyed by [position][audience]. `off` returns null
 * (no banner). `bold` is rendered as the eyebrow / lead-in; `body` is
 * the main paragraph; every message links to /pricing.
 */
export const NUDGE_MESSAGES = {
  off: null,

  progress_report: {
    free: {
      bold: "Progress reports are around the corner.",
      body:
        "You've been building lessons all fall — the right plan turns what " +
        "you've taught into report card comments, grounded in your own work " +
        "and written in Growing Success language. Take a look at which of " +
        "our plans fits what you need.",
    },
    basic: {
      bold: "Progress reports are around the corner.",
      body:
        "The right plan turns what you've taught this fall into report card " +
        "comments — grounded in your own work, written in Growing Success " +
        "language. See what upgrading unlocks.",
    },
  },

  term_1: {
    free: {
      bold: "Report card season is here.",
      body:
        "Writing comments from scratch is the worst part of the term — " +
        "it doesn't have to be. Find the plan that fits you, and the " +
        "lessons you've already built become report card comments you " +
        "can shape and make your own.",
    },
    basic: {
      bold: "Report card season is here.",
      body:
        "Writing comments from scratch is the worst part of the term — " +
        "it doesn't have to be. Upgrading takes the lessons you've " +
        "already built and turns them into report card comments you " +
        "can shape and make your own.",
    },
  },

  term_2: {
    free: {
      bold: "Second report cards, and you've got a whole year of lessons behind you.",
      body:
        "That's a lot of teaching to write up. See which of our plans " +
        "works for you — it turns all that work into report card comments " +
        "in minutes. Your language, your character limits, your judgment " +
        "on every word.",
    },
    basic: {
      bold: "Second report cards, and you've got a whole year of lessons behind you.",
      body:
        "That's a lot of teaching to write up. Upgrading turns all that " +
        "work into report card comments in minutes — your language, your " +
        "character limits, your judgment on every word.",
    },
  },
};

/**
 * Safe lookup — returns `null` for unknown / `off` positions, otherwise
 * the audience-specific message payload.
 */
export const getNudgeMessage = (position, audience) => {
  const payload = NUDGE_MESSAGES[position];
  if (!payload) return null;
  return payload[audience] || null;
};
